import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { t, formatMoney } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Copy, ScrollText, FileCheck2, Landmark } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";

export function BankDetails({ compact = false }) {
  const { lang } = useApp();
  const [bank, setBank] = useState(null);
  useEffect(() => { api.get("/config/bank").then(({ data }) => setBank(data)).catch(() => {}); }, []);
  if (!bank) return null;
  const copy = (val, label) => {
    navigator.clipboard.writeText(val);
    toast.success(`${t("copied", lang)}: ${label}`);
  };
  const Row = ({ label, value, testId }) => (
    <div className="flex items-center justify-between gap-4 border-b border-[#E5DDC9] py-2">
      <div>
        <p className="text-[10px] tracking-[0.2em] uppercase text-[#9A8B72]">{label}</p>
        <p className="text-sm text-[#1A1610] font-mono" data-testid={testId}>{value}</p>
      </div>
      <Button size="sm" variant="ghost" onClick={() => copy(value, label)} className="text-[#C5A880] hover:bg-[#EBE3D3] h-8">
        <Copy className="h-3.5 w-3.5 me-1" /> {t("copy", lang)}
      </Button>
    </div>
  );
  return (
    <div className={`rounded-lg border border-[#C5A880]/30 bg-[#EEE5D0] p-4 ${compact ? "" : "mt-2"}`} data-testid="bank-details">
      <div className="flex items-center gap-2 mb-2">
        <Landmark className="h-4 w-4 text-[#C5A880]" />
        <p className="text-xs tracking-[0.3em] uppercase text-[#C5A880]">{t("bank_details", lang)}</p>
      </div>
      <p className="text-xs text-[#7A6E5A] mb-3">{t("copy_iban_hint", lang)}</p>
      <Row label={t("beneficiary", lang)} value={bank.beneficiary} testId="bank-beneficiary" />
      <Row label={t("iban", lang)} value={bank.iban} testId="bank-iban" />
      <Row label={t("bic", lang)} value={bank.bic} testId="bank-bic" />
    </div>
  );
}

const EVENT_LABEL = {
  created: "event_created",
  deposit_received: "event_deposit_received",
  released: "event_released",
  refunded: "event_refunded",
  contract_signed: "event_contract_signed",
};

export function EscrowLedgerButton({ deal }) {
  const { lang } = useApp();
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/deals/${deal.id}/escrow/events`); setEvents(data); }
    catch { toast.error("Access denied"); }
  }, [deal.id]);
  useEffect(() => { if (open) load(); }, [open, load]);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`escrow-ledger-btn-${deal.id}`} className="border-[#B8A98F] text-[#4A3F30] bg-transparent hover:bg-[#EBE3D3] rounded-full">
          <ScrollText className="h-3.5 w-3.5 me-1.5" />
          {t("view_ledger", lang)}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#FFFAF0] border-[#D8CFBB] text-[#1A1610] max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-light" style={{ fontFamily: "Cormorant Garamond, serif" }}>{t("escrow_ledger", lang)}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-[#7A6E5A] mb-4">{deal.property_title} · {deal.buyer_name}</p>
        {!events.length && <p className="text-sm text-[#9A8B72] py-8 text-center">{t("no_events", lang)}</p>}
        <div className="relative ps-6 border-s border-[#D8CFBB]">
          {events.map((e, idx) => (
            <div key={e.id} className="relative pb-6" data-testid={`escrow-event-${idx}`}>
              <div className="absolute -start-[7px] top-1 h-3 w-3 rounded-full bg-[#C5A880] border-2 border-[#F5F0E6]" />
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-sm text-[#1A1610] font-medium">{t(EVENT_LABEL[e.event] || "no_events", lang)}</p>
                <p className="text-xs text-[#9A8B72] font-mono">{new Date(e.created_at).toLocaleString()}</p>
              </div>
              <p className="text-xs text-[#6B5F4C] mt-1">
                {e.actor_name} <span className="text-[#B8A98F]">· {e.actor_role}</span>
                {e.amount ? <span className="ms-2 text-[#C5A880]">{formatMoney(e.amount, lang)}</span> : null}
                {e.reference ? <span className="ms-2 text-[#9A8B72] font-mono">{e.reference}</span> : null}
              </p>
              {e.note && <p className="text-xs text-[#7A6E5A] mt-1 italic">{e.note}</p>}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DirectContractButton({ deal, onSigned }) {
  const { lang, user } = useApp();
  const [open, setOpen] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [signatureImg, setSignatureImg] = useState(null);

  if (!deal.is_direct) return null;
  const signed = deal.direct_contract_signed;
  const inputCls = "bg-[#F5F0E6] border-[#D8CFBB] text-[#1A1610] focus-visible:ring-[#C5A880]";

  const submit = async () => {
    try {
      await api.post(`/deals/${deal.id}/direct-contract/sign`, {
        signer_name: name,
        signer_email: email,
        accepted: true,
        signature_image_base64: signatureImg,
      });
      toast.success(t("direct_contract_signed", lang));
      setOpen(false); setAccepted(false); setSignatureImg(null);
      onSigned?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`direct-contract-btn-${deal.id}`}
          className={`rounded-full ${signed ? "border-emerald-600/60 text-emerald-400" : "border-[#C5A880]/50 text-[#C5A880]"} bg-transparent hover:bg-[#EBE3D3]`}>
          <FileCheck2 className="h-3.5 w-3.5 me-1.5" />
          {signed ? t("direct_contract_signed", lang) : t("sign_contract", lang)}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#FFFAF0] border-[#D8CFBB] text-[#1A1610] max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-2xl font-light" style={{ fontFamily: "Cormorant Garamond, serif" }}>{t("direct_contract", lang)}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="rounded-md border border-[#D8CFBB] bg-[#F5F0E6] p-4 text-xs text-[#4A3F30] leading-relaxed max-h-56 overflow-y-auto">
            <p className="uppercase tracking-widest text-[#C5A880] text-[10px] mb-2">AARON &amp; PARTNERS S.R.L. — P.IVA 01754670535</p>
            <p className="mb-2"><strong>{deal.property_title}</strong> · {deal.city || ""} · Prezzo € {Number(deal.sale_price).toLocaleString()}</p>
            <p>{t("direct_contract_body", lang)}</p>
            {deal.direct_contract_signature && (
              <div className="mt-3 pt-3 border-t border-[#D8CFBB] text-[#7A6E5A]">
                <p>✓ {deal.direct_contract_signature.signer_name} &lt;{deal.direct_contract_signature.signer_email}&gt;</p>
                <p className="font-mono text-[11px]">{new Date(deal.direct_contract_signature.signed_at).toLocaleString()}</p>
              </div>
            )}
          </div>
          {!signed && (
            <>
              <div><Label className="text-[#4A3F30]">{t("signer_name", lang)}</Label><Input data-testid="signer-name-input" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} /></div>
              <div><Label className="text-[#4A3F30]">{t("signer_email", lang)}</Label><Input data-testid="signer-email-input" className={inputCls} type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
              <SignaturePad onChange={setSignatureImg} />
              <div className="flex items-center gap-3">
                <Checkbox id="dc-accept" checked={accepted} onCheckedChange={setAccepted} data-testid="direct-contract-accept" />
                <Label htmlFor="dc-accept" className="text-[#2E2A22] cursor-pointer text-sm">Accetto e firmo</Label>
              </div>
            </>
          )}
        </div>
        {!signed && (
          <DialogFooter>
            <Button disabled={!accepted || !name || !email} onClick={submit} data-testid="direct-contract-submit"
              className="bg-[#C5A880] text-black hover:bg-[#d9bf94] rounded-full disabled:opacity-40">
              {t("sign_contract", lang)}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
